/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : VlcMain.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */

import React, { useEffect, useRef, useState, useCallback } from "react";
import { HandleApiActions, labCar } from "../../../../redux/actions";

import { shallowEqual, useDispatch, useSelector } from "react-redux";
import customConsole from "../../../../config/customConsole";
import { useLocation, useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import VlcEEComponents from "../../../../pages/labcar/EEComponents";
import selectVariantAction from "../../../../redux/actions/selectVariant";

function VlcMain({
  selectedSingleVariantDetails,
  setSelectedSingleVariantDetails,
  projectOptionsList,
  setProjectOptionsList,
  selectedVariant,
  setSelectedVariant,
  selectedProject,
  setSelectedProject,
  selectedProjectDetails,
  selectedVariantDetails,
  variantOptionList,
  projectDetails,
  projectId,
  setSelectedVehicleView,
  selectedVehicleView,
  selectedEEComponentDetails,
  setSelectedEEComponentDetails,
  selectedZone,
  setSelectedZone,
  selectedZoneDetails,
  setSelectedZoneDetails,
  getApiDataforFilter,
  setGetApiDataforFilter,
  filterData,
  zoneEECountList,
  setZoneEECountList,
  setProjectEECount,
  getZoneEECountList,
}) {
  /** ============== Redux ============== **/
  const dispatch = useDispatch();
  const navigate = useNavigate();

  /** ============== VehicleSystems ============== **/
  const settingsVehicleSystemsDetails = useSelector(
    (state) => state.vehicleSystemsReducer,
  );


  /* Extracting EE components list from the Redux store */
  const { eeComponentsList } = useSelector(
    (state) => state.labCarReducer,
    shallowEqual,
  );

  const eeData = useSelector((state) => state.labCarReducer, shallowEqual);



  /* State for handling the software modules screen */
  const [, setIsLinkSoftwareModulesScreen] = useState(false);

  useEffect(() => {
    const projectId = selectedProjectDetails?.project_id;
    const variantId =
      selectedSingleVariantDetails?.variant_id || selectedVariant?.variant_id;
    if (projectId && variantId) {
      setSelectedVehicleView("ECU");
    }
  }, [
    selectedProjectDetails?.project_id,
    selectedSingleVariantDetails?.variant_id,
    selectedVariant?.variant_id,
    setSelectedVehicleView,
  ]);

  const [vsAndVCOptionsList, setVSAndVCOptionsList] = useState([]);

  /* Zone - EE */
  const [zoneEEList, setZoneEEList] = useState([]);
  /* Centralized - EE */
  const [centralizedEEList, setCentralizedEEList] = useState([]);

  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const selectedEEIdFromUrl = queryParams.get("ee_id");
  const selectedPageFromUrl = queryParams.get("page");
  const selectedZoneFromUrl = queryParams.get("selectedZone");
  const zonePositionFromUrl = queryParams.get("zonePosition");
  const actionModeFromUrl = queryParams.get("actionMode");
  const selectedSWMIdFromUrl = queryParams.get("swm_id");
  const isRedirectedEEAction =
    actionModeFromUrl === "eeComponentAction" && Boolean(selectedEEIdFromUrl);
  const isRedirectedSWMAction =
    actionModeFromUrl === "swmAction" && Boolean(selectedSWMIdFromUrl);
  const queryLinkSoftwareModulesScreen = queryParams.get(
    "linkSoftwareModulesScreen",
  );

  // Filtering
  const [selectedSearchField, setSelectedSearchField] =
    useState("supplier_name");
  const [searchFieldValue, setSearchFieldValue] = useState("");

  // Pagination
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [totalEECount, setTotalEECount] = useState(0);

  const [swmPage, setSwmPage] = useState(1);
  const [swmRowsPerPage, setSwmRowsPerPage] = useState(25);
  const [hasMore, setHasMore] = useState(false);
  const [swmHasMore, setSwmHasMore] = useState(false);
  const [totalSWMCount, setTotalSWMCount] = useState(0);

  useEffect(() => {
    const pageFromUrl = Number(selectedPageFromUrl);
    if (pageFromUrl > 0 && page !== pageFromUrl) {
      setPage(pageFromUrl);
    }
  }, [selectedPageFromUrl, page, setPage]);

  useEffect(() => {
    if (queryLinkSoftwareModulesScreen) {
      setIsLinkSoftwareModulesScreen(true);
    }
  }, [queryLinkSoftwareModulesScreen]);

  /* useEffects */
  const prevProjectIdRef = useRef();
  useEffect(() => {
    const projectId = selectedProject?.project_id;

    // On first mount (including when coming back from Vehicle Systems),
    // do not clear parent-held selections.
    if (prevProjectIdRef.current === undefined) {
      prevProjectIdRef.current = projectId;
      return;
    }

    // Reset only when the project actually changes.
    if (prevProjectIdRef.current !== projectId) {
      prevProjectIdRef.current = projectId;
      setSelectedZone(null);
      setSelectedZoneDetails(null);
      setZoneEEList([]);
      setCentralizedEEList([]);
      setSelectedSingleVariantDetails([]);
    }
  }, [
    selectedProject?.project_id,
    setSelectedSingleVariantDetails,
    setSelectedZone,
    setSelectedZoneDetails,
  ]);



  /**
   * @author : Narender
   * @Date : 24-03-2025
   * @description : Fetches vehicle systems for zones vs count
   * @param {project_id, variant_id}
   * @return {msg: "", data: [], error: true | false}
   */


  const prevProjectDetailsIdRef = useRef();
  useEffect(() => {
    const projectId = selectedProjectDetails?.project_id;
    if (prevProjectDetailsIdRef.current === undefined) {
      prevProjectDetailsIdRef.current = projectId;
      return;
    }
    if (prevProjectDetailsIdRef.current !== projectId) {
      prevProjectDetailsIdRef.current = projectId;
      dispatch(selectVariantAction(null));
    }
  }, [selectedProjectDetails?.project_id, dispatch]);



  useEffect(() => {
    /* Update selected zone details based on the selected zone */
    if (
      selectedZone &&
      selectedZone?.zone_position_name &&
      selectedProjectDetails &&
      selectedProjectDetails?.project_zones
    ) {
      let filteredSelectedZoneDetails =
        selectedProjectDetails?.project_zones?.find(
          (el) => el?.zone_name === selectedZone?.zone_position_name,
        );
      setSelectedZoneDetails(filteredSelectedZoneDetails);
    }
  }, [selectedZone, selectedProjectDetails, setSelectedZoneDetails]);

  const buildEEFetchParams = ({
    projectId,
    variantId,
    isZonal,
    zonePosition,
    eeId,
    searchValue,
    searchField,
    pageNumber,
    pageSize,
  }) => {
    if (!projectId || !variantId) return null;
    if (isZonal && !zonePosition) return null;

    return {
      project_id: projectId,
      variant_id: Number(variantId),
      ...(isZonal && { zone_position: zonePosition }),
      ...(eeId && { ee_id: Number(eeId) }),
      ...(searchValue && { [searchField]: searchValue }),
      page_number: pageNumber,
      page_size: pageSize,
    };
  };

  const updateEEListState = (data, isZonal) => {
    if (!Array.isArray(data)) return;
    if (isZonal) {
      setZoneEEList(data);
    } else {
      setCentralizedEEList(data);
    }
  };

  const getEEComponentsList = useCallback(async () => {
    /* Fetch EE components based on the selected project and zone details */
    const projectId = selectedProjectDetails?.project_id;
    const variantId = selectedSingleVariantDetails?.variant_id;
    const isZonal = !!selectedProjectDetails?.is_zonal;
    const zonePosition = isZonal ? selectedZoneDetails?.position : undefined;
    const isRedirectedActionZone =
      !isZonal ||
      String(selectedZoneDetails?.position) === String(zonePositionFromUrl) ||
      selectedZoneDetails?.zone_name === selectedZoneFromUrl;
    const redirectedActionEEId =
      (isRedirectedEEAction || isRedirectedSWMAction) && isRedirectedActionZone
        ? selectedEEIdFromUrl
        : null;

    const params = buildEEFetchParams({
      projectId,
      variantId,
      isZonal,
      zonePosition,
      eeId: redirectedActionEEId,
      searchValue: searchFieldValue,
      searchField: selectedSearchField,
      pageNumber: page,
      pageSize: rowsPerPage,
    });

    if (!params) return;

    const eeResponse = await dispatch(
      HandleApiActions({
        ...labCar.get_ee_components,
        params,
        show_toast: false,
      }),
    );
    setTotalEECount(eeResponse.total_count);

    if (eeResponse?.error) {
      return;
    }

    setHasMore(eeResponse.data.length >= rowsPerPage);
    // Immediately mirror into local lists to ensure UI updates
    updateEEListState(eeResponse?.data, isZonal);

    const redirectedActionCompleted =
      isRedirectedEEAction &&
      redirectedActionEEId &&
      eeResponse?.data?.length === 1 &&
      ![4, 5].includes(Number(eeResponse.data[0]?.action_type));

    if (redirectedActionCompleted) {
      const paramsToClear = new URLSearchParams(location.search);
      paramsToClear.delete("ee_id");
      paramsToClear.delete("actionMode");
      paramsToClear.delete("page");
      navigate(`${location.pathname}?${paramsToClear.toString()}`, {
        replace: true,
      });
    }
  }, [
    selectedProjectDetails?.project_id,
    selectedSingleVariantDetails?.variant_id,
    selectedProjectDetails?.is_zonal,
    selectedZoneDetails?.position,
    selectedZoneDetails?.zone_name,
    page,
    rowsPerPage,
    searchFieldValue,
    selectedSearchField,
    selectedEEIdFromUrl,
    selectedZoneFromUrl,
    zonePositionFromUrl,
    isRedirectedEEAction,
    isRedirectedSWMAction,
    location.pathname,
    location.search,
    navigate,
    dispatch,
  ]);

  const lastEEFetchKeyRef = useRef("");
  // Ensure EE fetch is not suppressed when switching back to EE view
  useEffect(() => {
    if (selectedVehicleView === "ECU") {
      lastEEFetchKeyRef.current = "";
    }
  }, [selectedVehicleView]);
  useEffect(() => {
    if (!actionModeFromUrl) {
      lastEEFetchKeyRef.current = "";
    }
  }, [actionModeFromUrl]);
  useEffect(() => {
    const projectId = selectedProjectDetails?.project_id;
    const variantId = selectedSingleVariantDetails?.variant_id;
    const isZonal = !!selectedProjectDetails?.is_zonal;
    const zoneKey = isZonal
      ? String(selectedZoneDetails?.position || "")
      : "CENTRAL";
    const key = [
      projectId,
      variantId,
      zoneKey,
      page,
      rowsPerPage,
      selectedSearchField,
      searchFieldValue,
      selectedEEIdFromUrl,
      selectedSWMIdFromUrl,
      actionModeFromUrl,
    ].join("|");

    if (!projectId || !variantId || (isZonal && !selectedZoneDetails?.position))
      return;
    // Guard: ensure variant belongs to the currently selected project
    const variantProjectId = selectedSingleVariantDetails?.project_id;
    if (variantProjectId && Number(variantProjectId) !== Number(projectId)) {
      return;
    }
    if (lastEEFetchKeyRef.current === key) return;
    lastEEFetchKeyRef.current = key;

    getEEComponentsList();
  }, [
    selectedProjectDetails?.project_id,
    selectedSingleVariantDetails?.variant_id,
    selectedSingleVariantDetails?.project_id,
    selectedProjectDetails?.is_zonal,
    selectedZoneDetails?.position,
    page,
    rowsPerPage,
    selectedSearchField,
    searchFieldValue,
    selectedEEIdFromUrl,
    selectedSWMIdFromUrl,
    actionModeFromUrl,
    getEEComponentsList,
  ]);

  useEffect(() => {
    // Mirror EE components into local lists only when a valid variant is selected
    const hasVariant = Boolean(selectedSingleVariantDetails?.variant_id);
    if (!selectedProjectDetails) return;
    if (!hasVariant) {
      setZoneEEList([]);
      setCentralizedEEList([]);
      setTotalEECount(0);
      setTotalSWMCount(0);
      return;
    }

    if (eeComponentsList) {
      if (selectedProjectDetails.is_zonal) {
        setZoneEEList(eeComponentsList);
      } else {
        setCentralizedEEList(eeComponentsList);
      }
    }
  }, [
    eeComponentsList,
    selectedProjectDetails,
    selectedSingleVariantDetails?.variant_id,
  ]);


  // Removed unused supplier options effect

  /** ============== VehicleSystems ============== **/
  useEffect(() => {
    let filteredVehicleSystemOptionsList = Array.isArray(
      settingsVehicleSystemsDetails?.vehicleSystemsDetails?.data,
    )
      ? settingsVehicleSystemsDetails.vehicleSystemsDetails.data.map((vs) => ({
        value: vs?.name,
        label: vs?.name,
        vehicleComponentsOptionsList: Array.isArray(vs?.components)
          ? vs.components.map((vc) => ({
            value: vc,
            label: vc,
          }))
          : [],
      }))
      : [];

    setVSAndVCOptionsList(filteredVehicleSystemOptionsList);
  }, [settingsVehicleSystemsDetails]);
  // Removed unused selectedVSName state updater

  useEffect(() => {
  }, [zoneEECountList]);
  useEffect(() => {
    customConsole("selectedZoneDetails: " + selectedZoneDetails);
    customConsole("selectedZone: " + selectedZone);
    customConsole("selectedProjectDetails: " + selectedProjectDetails);
    customConsole("zoneEEList " + zoneEEList);
    customConsole({ zoneEECountList });
    customConsole({ vsAndVCOptionsList });
  }, [
    selectedZoneDetails,
    zoneEEList,
    zoneEECountList,
    vsAndVCOptionsList,
    selectedProjectDetails,
    selectedZone,
  ]);


  /* -------------- Handle Zone EE functions -------------- */
  /* Add a new EE component */
  const handleZoneEEAdd = ({
    variant_ids,
    save_as_draft,
    vc_id,
    assembly_number,
    supplier_name,
    version,
    summary,
    description,
    vehicle_systems,
    other_fields,
    attributes,
  }) => {
    if (
      selectedProjectDetails &&
      selectedProjectDetails?.project_id &&
      selectedSingleVariantDetails &&
      selectedSingleVariantDetails?.variant_id
    ) {
      let currentTime = new Date().toISOString();

      return dispatch(
        HandleApiActions({
          ...labCar.add_ee_component,
          params: {
            variant_ids,
            save_as_draft,
            project_id: Number(selectedProjectDetails?.project_id),
            variant_id: Number(selectedSingleVariantDetails?.variant_id),
            ...(selectedProjectDetails.is_zonal && {
              zone_position: selectedZoneDetails?.position,
              // zone_sequence: selectedZoneDetails.sequence,
            }),
            assembly_number,
            ...(supplier_name && { supplier_name }),
            vc_id: vc_id,
            version,
            ...(summary && { summary }),
            description,
            vehicle_systems,
            other_fields,
            current_time: currentTime,
            attributes: attributes,
          },
          show_toast: true,
        }),
      ).then((res) => {
        getEEComponentsList();
        // getZoneEECountList is defined in VlcIndex and requires projectId + variantId
        getZoneEECountList(
          Number(selectedProjectDetails?.project_id),
          Number(selectedSingleVariantDetails?.variant_id),
        );

        return !res.error;
      });
    }

    return false;
  };

  /* Update an existing EE component */
  const handleZoneEEUpdate = ({ ee_id, version, will_it_impact, reason }) => {
    if (
      selectedProjectDetails &&
      selectedProjectDetails?.project_id &&
      selectedSingleVariantDetails &&
      selectedSingleVariantDetails?.variant_id
    ) {
      let currentTime = new Date().toISOString();

      customConsole({
        project_id: Number(selectedProjectDetails?.project_id),
        ee_id,
        version,
        current_time: currentTime,
      });

      dispatch(
        HandleApiActions({
          ...labCar.update_ee_component,
          params: {
            project_id: Number(selectedProjectDetails?.project_id),
            variant_id: Number(selectedSingleVariantDetails?.variant_id),
            ee_id,
            version,
            will_it_impact,
            reason,
            current_time: currentTime,
          },
          show_toast: true,
        }),
      ).then(() => {
        getEEComponentsList();
      });
    }
  };

  /* Remove an EE component */
  const handleZoneEEReplace = ({
    ee_id,
    assembly_number,
    supplier_name,
    version,
    summary,
    description,
    vehicle_systems,
    other_fields,
    will_it_impact,
    reason,
  }) => {
    if (
      selectedProjectDetails &&
      selectedProjectDetails?.project_id &&
      selectedSingleVariantDetails &&
      selectedSingleVariantDetails?.variant_id
    ) {
      let currentTime = new Date().toISOString();

      customConsole({
        project_id: Number(selectedProjectDetails?.project_id),
        ee_id,
        assembly_number,
        supplier_name,
        version,
        ...(summary && { summary }),
        description,
        vehicle_systems,
        other_fields,
        current_time: currentTime,
      });

      dispatch(
        HandleApiActions({
          ...labCar.replace_ee_component,
          params: {
            project_id: Number(selectedProjectDetails?.project_id),
            variant_id: Number(selectedSingleVariantDetails?.variant_id),
            ee_id,
            assembly_number,
            supplier_name,
            version,
            ...(summary && { summary }),
            description,
            vehicle_systems,
            other_fields,
            reason,
            will_it_impact,
            current_time: currentTime,
          },
          show_toast: true,
        }),
      ).then(() => {
        getEEComponentsList();
      });
    }
  };

  // 1. EE Filter Sidebar State
  // const [eeFilterDrawerOpen, setEEFilterDrawerOpen] = useState(false);
  const [eeSupplierFilter, setEESupplierFilter] = useState([]);
  const [eeAssemblyNumFilter, setEEAssemblyNumFilter] = useState([]);
  const [eeVersionFilter, setEEVersionFilter] = useState([]);
  // const [eeSupplierFilterSearch, setEESupplierFilterSearch] = useState("");
  // const [eeAssemblyNumFilterSearch, setEEAssemblyNumFilterSearch] =
  //   useState("");
  // const [eeVersionFilterSearch, setEEVersionFilterSearch] = useState("");
  // EE Sort State
  const [eeSortField, setEESortField] = useState("assembly_number");
  const [eeSortOrder, setEESortOrder] = useState("asc");

  // 2. Handler functions for EE filter sidebar
  // const handleEEFilterClose = () => setEEFilterDrawerOpen(false);
  // const handleEEFilterOpen = () => setEEFilterDrawerOpen(true);

  const handleEEClearFilters = () => {
    setPage(1);
    setEESupplierFilter([]);
    setEEAssemblyNumFilter([]);
    setEEVersionFilter([]);
    setEESortField("assembly_number");
    setEESortOrder("asc");
    setSWMNameFilter([]);
    setSWMVersionFilter([]);
    setSWMNameFilterSearch("");
    setSWMVersionFilterSearch("");
    setSWMSortField("name");
    setSWMSortOrder("asc");
  };

  // 3. Filter the EE components list using the sidebar filter state
  // Use correct EE list depending on project type
  const eeListToFilter = selectedProjectDetails?.is_zonal
    ? zoneEEList
    : centralizedEEList;
  const filteredEEList = eeListToFilter.filter((item) => {
    const supplierOk =
      eeSupplierFilter.length === 0 ||
      eeSupplierFilter.includes(item.supplier_name);
    const assemblyOk =
      eeAssemblyNumFilter.length === 0 ||
      eeAssemblyNumFilter.includes(item.assembly_number);
    const versionOk =
      eeVersionFilter.length === 0 || eeVersionFilter.includes(item.version);
    return supplierOk && assemblyOk && versionOk;
  });
  // Sort filteredEEList by selected field and order
  const sortedEEList = [...filteredEEList].sort((a, b) => {
    const field = eeSortField;
    let aVal = a[field] || "";
    let bVal = b[field] || "";
    // Try numeric sort if possible
    if (!isNaN(Number(aVal)) && !isNaN(Number(bVal))) {
      aVal = Number(aVal);
      bVal = Number(bVal);
    }
    if (aVal < bVal) return eeSortOrder === "asc" ? -1 : 1;
    if (aVal > bVal) return eeSortOrder === "asc" ? 1 : -1;
    return 0;
  });




  // === SWM Filter State ===
  const [swmNameFilter, setSWMNameFilter] = useState([]);
  const [swmVersionFilter, setSWMVersionFilter] = useState([]);
  const [swmNameFilterSearch, setSWMNameFilterSearch] = useState("");
  const [swmVersionFilterSearch, setSWMVersionFilterSearch] = useState("");
  const [swmSortField, setSWMSortField] = useState("name");
  const [swmSortOrder, setSWMSortOrder] = useState("asc");

  // === SWM Filtered List (local, for UI logic) ===
  // Find the selected EE component (from filtered/sorted EE list)
  // Removed unused selectedEEComponent reference

  const { swModulesList } = useSelector(
    (state) => state.labCarReducer,
    shallowEqual,
  );

  // Use its software modules for the filter options
  const swmListToFilter = swModulesList || [];



  const filteredSWMList = swmListToFilter.filter((item) => {
    const nameOk =
      swmNameFilter.length === 0 || swmNameFilter.includes(item.name);
    const versionOk =
      swmVersionFilter.length === 0 || swmVersionFilter.includes(item.version);
    return nameOk && versionOk;
  });
  const sortedSWMList = [...filteredSWMList].sort((a, b) => {
    const field = swmSortField;
    let aVal = a[field] || "";
    let bVal = b[field] || "";
    if (typeof aVal === "string") aVal = aVal.toLowerCase();
    if (typeof bVal === "string") bVal = bVal.toLowerCase();
    if (aVal < bVal) return swmSortOrder === "asc" ? -1 : 1;
    if (aVal > bVal) return swmSortOrder === "asc" ? 1 : -1;
    return 0;
  });

  // Removed unused SWM options arrays

  // 1. Vehicle Systems filter state and handlers
  // Removed unused Vehicle Systems search state/handlers

  // 2. Vehicle Components filter state and handlers
  // Removed unused Vehicle Components search state/handlers

  // ...inside LabCarMain...

  // Vehicle Systems Filter Sidebar State
  // Removed unused Vehicle Systems filter sidebar state/handlers

  // Vehicle Components Filter Sidebar State
  // Removed unused Vehicle Components filter sidebar state/handlers

  // Removed unused Vehicle Systems input states and effects

  // Removed unused Vehicle Components input states and effects

  // 1. Clear filters when project, variant, or zone changes
  useEffect(() => {
    handleEEClearFilters();
  }, [
    selectedProjectDetails?.project_id,
    selectedSingleVariantDetails?.variant_id,
    selectedZoneDetails?.zone_name,
  ]);

  // 2. Clear filters when switching between CAR/MIND MAP view
  // Removed unused selectedView filter reset effect

  // 3. Clear filters when switching between EE/Vehicle architecture
  useEffect(() => {
    handleEEClearFilters();
  }, [selectedVehicleView]);



  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
        <VlcEEComponents
          getZoneEECountList={getZoneEECountList}
          variantOptionList={variantOptionList}
          selectedSingleVariantDetails={selectedSingleVariantDetails}
          heading={
            selectedProjectDetails?.is_zonal
              ? selectedZoneDetails?.zone_name
              : "Centralized"
          }
          eEComponentsList={sortedEEList}
          handleEEComponentAdd={handleZoneEEAdd}
          handleEEComponentUpdate={handleZoneEEUpdate}
          handleEEComponentReplace={handleZoneEEReplace}
          selectedProjectDetails={selectedProjectDetails}
          selectedZoneDetails={selectedZoneDetails}
          selectedZone={selectedZone}
          setIsLinkSoftwareModulesScreen={setIsLinkSoftwareModulesScreen}
          getEEComponentsList={getEEComponentsList}
          searchFieldValue={searchFieldValue}
          setSearchFieldValue={setSearchFieldValue}
          selectedSearchField={selectedSearchField}
          setSelectedSearchField={setSelectedSearchField}
          hasMore={hasMore}
          setHasMore={setHasMore}
          rowsPerPage={rowsPerPage}
          swmHasMore={swmHasMore}
          setSwmHasMore={setSwmHasMore}
          setRowsPerPage={setRowsPerPage}
          swmRowsPerPage={swmRowsPerPage}
          setSwmRowsPerPage={setSwmRowsPerPage}
          page={page}
          swmPage={swmPage}
          setPage={setPage}
          setSwmPage={setSwmPage}
          swmNameFilter={swmNameFilter}
          setSWMNameFilter={setSWMNameFilter}
          swmVersionFilter={swmVersionFilter}
          setSWMVersionFilter={setSWMVersionFilter}
          swmNameFilterSearch={swmNameFilterSearch}
          setSWMNameFilterSearch={setSWMNameFilterSearch}
          swmVersionFilterSearch={swmVersionFilterSearch}
          setSWMVersionFilterSearch={setSWMVersionFilterSearch}
          swmSortField={swmSortField}
          setSWMSortField={setSWMSortField}
          swmSortOrder={swmSortOrder}
          setSWMSortOrder={setSWMSortOrder}
          swmListToFilter={swmListToFilter}
          filteredSWMList={filteredSWMList}
          sortedSWMList={sortedSWMList}
          selectedEEComponentDetails={selectedEEComponentDetails}
          setSelectedEEComponentDetails={setSelectedEEComponentDetails}
          totalEECount={totalEECount}
          setTotalEECount={setTotalEECount}
          totalSWMCount={totalSWMCount}
          setTotalSWMCount={setTotalSWMCount}
        />
      </Box>

    </Box>
  );
}

export default React.memo(VlcMain);
